// A video's view: zoom (1 shows the whole frame) and the point of the frame shown at the
// centre, as fractions of its width and height. One half of the ice at a practice is a
// zoomed view anchored to that side. The view only changes how the player shows the video;
// the video itself, its timeline and its clips are unchanged.
export const MIN_ZOOM = 1
export const MAX_ZOOM = 4
export const FULL_VIEW = Object.freeze({ zoom: 1, centerX: 0.5, centerY: 0.5 })
// A half view runs from the centre line to just past the net, leaving out the boards and seats
// in the far corner of a wide rink-camera frame. Measured from the owner's own adjusted view of
// a practice (2026-11-05): zoom 1.8, centred 69% across and 55% down (31% for the left half).
// 2.2x anchored to the far edge (the first try) cut off the centre line.
export const HALF_ZOOM = 1.8
export const HALF_CENTER_X = 0.69
export const HALF_CENTER_Y = 0.55

const clamp = (value, low, high) => Math.min(Math.max(value, low), high)
const finite = (value, fallback) => Number.isFinite(value) ? value : fallback

export const isZoomed = view => Boolean(view) && view.zoom > MIN_ZOOM + 0.001

// Keep the frame filling the player: the shown window never runs past an edge of the video.
export const clampView = view => {
  const zoom = clamp(finite(view?.zoom, 1), MIN_ZOOM, MAX_ZOOM)
  const half = 0.5 / zoom
  return {
    zoom,
    centerX: clamp(finite(view?.centerX, 0.5), half, 1 - half),
    centerY: clamp(finite(view?.centerY, 0.5), half, 1 - half)
  }
}

// Each preset is the same framing every time, so choosing it again also replaces an older,
// looser half view saved with the video. A different framing is what Adjust manually is for.
export const presetView = name => {
  if (name === 'full') return { ...FULL_VIEW }
  return clampView({ zoom: HALF_ZOOM, centerY: HALF_CENTER_Y, centerX: name === 'left' ? 1 - HALF_CENTER_X : HALF_CENTER_X })
}

export const viewName = view => {
  if (!isZoomed(view)) return 'Full frame'
  if (sameView(view, presetView('left'))) return 'Left half'
  if (sameView(view, presetView('right'))) return 'Right half'
  return 'Custom'
}

// The CSS transform for the video (and anything drawn over it): with transform-origin at the
// centre, translate brings the chosen centre to the middle and scale zooms around it.
export const viewTransform = view => {
  if (!isZoomed(view)) return 'none'
  const { zoom, centerX, centerY } = clampView(view)
  const pct = value => `${Math.round(value * 10000) / 100}%`
  return `scale(${Math.round(zoom * 1000) / 1000}) translate(${pct(0.5 - centerX)}, ${pct(0.5 - centerY)})`
}

// Dragging by a fraction of the player's size moves the picture with the pointer.
export const panView = (view, dx, dy) => clampView({
  ...view,
  centerX: view.centerX - dx / view.zoom,
  centerY: view.centerY - dy / view.zoom
})

// Zoom by a factor, keeping the point under the anchor (a fraction of the player) where it is.
export const zoomView = (view, factor, anchor = { x: 0.5, y: 0.5 }) => {
  const zoom = clamp(view.zoom * factor, MIN_ZOOM, MAX_ZOOM)
  const pointX = view.centerX + (anchor.x - 0.5) / view.zoom
  const pointY = view.centerY + (anchor.y - 0.5) / view.zoom
  return clampView({ zoom, centerX: pointX - (anchor.x - 0.5) / zoom, centerY: pointY - (anchor.y - 0.5) / zoom })
}

export const sameView = (a, b) => ['zoom', 'centerX', 'centerY'].every(key => Math.abs((a?.[key] ?? 0) - (b?.[key] ?? 0)) < 0.0005)

export const viewFromApi = body => body ? clampView({ zoom: body.zoom, centerX: body.center_x, centerY: body.center_y }) : { ...FULL_VIEW }
export const viewToApi = view => {
  const { zoom, centerX, centerY } = clampView(view)
  return { zoom, center_x: centerX, center_y: centerY }
}
